'use client';

import { useState, useEffect, useRef } from 'react';
import { Stack, HStack } from '@terra/ui-styled-system';
import { LegacyDialog, Button, Textarea } from '@terra/ui-design-system';
import { useProxyStore } from '../../store/proxy-store';

type CommentDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  requestId: string;
  currentComment?: string;
};

export function CommentDialog({
  isOpen,
  onClose,
  requestId,
  currentComment,
}: CommentDialogProps) {
  const [comment, setComment] = useState('');
  const setRequestComment = useProxyStore((state) => state.setRequestComment);
  const currentCommentRef = useRef(currentComment);

  currentCommentRef.current = currentComment;

  useEffect(() => {
    if (isOpen) {
      setComment(currentCommentRef.current || '');
    }
  }, [isOpen]); // Seed from the latest comment only when the dialog opens

  const handleSave = () => {
    setRequestComment(requestId, comment);
    onClose();
  };

  const handleClear = () => {
    setRequestComment(requestId, '');
    onClose();
  };

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={({ open }) => !open && onClose()}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content maxWidth="500px">
          <Stack gap="6" p="6">
            {/* Title & Description */}
            <Stack gap="1">
              <LegacyDialog.Title>Request Comment</LegacyDialog.Title>
              <LegacyDialog.Description>
                Add notes or observations about this request
              </LegacyDialog.Description>
            </Stack>

            {/* Content */}
            <Stack gap="4">
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Enter comment..."
                minHeight="120px"
                fontSize="sm"
                autoFocus
              />
            </Stack>

            {/* Actions */}
            <HStack gap="2" justifyContent="flex-end">
              {currentComment && (
                <Button variant="ghost" size="sm" onClick={handleClear}>
                  Clear
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave}>
                Save
              </Button>
            </HStack>
          </Stack>

          <LegacyDialog.CloseTrigger />
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
